import React from 'react';

import { LinearProgress, Typography } from '@mui/material';
import {
  QUESTION_RUN_PROGRESS_LABEL,
  useQuestionRunBarDisplay,
} from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/question-run-progress-bar';
import { AlineButton } from 'src/components/generic/AlineButton';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { makeStyles } from 'tss-react/mui';

import { useReportRunProgress } from './use-report-run-progress';

const useStyles = makeStyles()(theme => ({
  bar: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: 2,
    height: 4,
  },
  caption: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: '16px',
  },
  progress: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  pulse: {
    flexShrink: 0,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginRight: theme.spacing(1.5),
    width: 300,
  },
  stopButton: {
    fontSize: 12,
    gap: theme.spacing(0.75),
    height: 24,
    minWidth: 0,
    padding: theme.spacing(0, 1),
  },
  stopSquare: {
    backgroundColor: 'currentColor',
    borderRadius: 2,
    flexShrink: 0,
    height: 8,
    width: 8,
  },
}));

/**
 * The report's active AI run, shown left of the header buttons so they never
 * move: a pulse, how many documents are done, and a Stop
 * that opens the confirmation. Renders nothing while nothing runs.
 */
export const ReportRunStatus = React.memo(function ReportRunStatus({
  filterModel,
  onStop,
  reportId,
  runningQuestionIds,
}: {
  filterModel: unknown;
  onStop: () => void;
  reportId: string;
  runningQuestionIds: string[];
}) {
  const { classes } = useStyles();
  const { progress } = useReportRunProgress({ filterModel, reportId, runningQuestionIds });
  const display = useQuestionRunBarDisplay(
    runningQuestionIds.length ? { progress, total: progress?.total ?? 0 } : null
  );

  if (!display) return null;

  return (
    <div aria-label="Aline AI is running" className={classes.root} role="status">
      <PulseIcon className={classes.pulse} variant={PulseIconVariant.LoadingPrimary} />
      <div className={classes.progress}>
        <Typography noWrap className={classes.caption} component="span">
          {display.caption ?? 'Starting Aline AI…'}
        </Typography>
        <LinearProgress
          aria-label={QUESTION_RUN_PROGRESS_LABEL}
          className={classes.bar}
          value={display.percent}
          variant="determinate"
        />
      </div>
      <AlineButton
        buttonClassName={classes.stopButton}
        color="primary"
        size="small"
        variant="text"
        onClick={onStop}
      >
        <span aria-hidden className={classes.stopSquare} />
        Stop
      </AlineButton>
    </div>
  );
});
